import Head from "next/head";
import React, { useEffect } from "react";
import { client } from "../../lib/sanity.client";
import imageUrlBuilder from "@sanity/image-url";
import { PortableText } from "@portabletext/react";
import Image from "next/image";
import dayjs from "dayjs";
import Link from "next/link";
import { AiOutlineArrowRight } from "react-icons/ai";
import { useRouter } from "next/router";

import { getOGimage, getUrl } from "../../utils/getUrl";
import { BsDot } from "react-icons/bs";
import { capture } from "../../utils/analytics/capture";

function urlFor(source) {
  return imageUrlBuilder(client).image(source);
}
function Post({ post }) {
  const router = useRouter();

  useEffect(() => {
    if (router != null) {
      capture({
        category: "browse",
        event: "page_view",
        properties: {
          id: post?._id,
          slug: post?.slug.current,
          title: post?.title,
          page_type: "blog_post",
        },
      });
    }
  }, []);
  const ptComponents = {
    marks: {
      internalLink: ({ value, children }) => {
        const { slug = {} } = value;

        const href = `/blog/${slug.current}`;
        return (
          <a href={href} target="_blank" rel="noreferrer">
            {children}
          </a>
        );
      },
      link: ({ value, children }) => {
        const { blank, href } = value;

        return blank ? (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        ) : (
          <a href={href} target="_blank" rel="noreferrer">
            {children}
          </a>
        );

        // <Link href={href}>{children}</Link>
      },
    },
    types: {
      image: ({ value }) => {
        if (!value?.asset?._ref) {
          return null;
        }
        const src = urlFor(value).url();

        return (
          <div className=" relative my-0 h-64 w-full rounded-md  lg:h-96">
            <Image
              alt={value.alt || " "}
              className=" h-full w-full rounded-md object-cover  object-top"
              layout="fill"
              src={src}
              // src={urlFor(value).width(320).height(240).fit("max").auto("format")}
            />
          </div>
        );
      },
    },
  };

  const schemaData = {
    "@context": "https://schema.org/",
    "@type": "BlogPosting",
    "@id": `${getUrl()}/blog/${post?.slug.current}/#BlogPosting`,
    mainEntityOfPage: `${getUrl()}/blog/${post?.slug.current}`,
    headline: post?.title,
    name: post?.title,
    description: post?.description,
    datePublished: dayjs(post?.publishedAt).format("YYYY-MM-DD"),
    dateModified: dayjs(
      post?.lastUpdatedAt ? post?.lastUpdatedAt : post?.publishedAt
    ).format("YYYY-MM-DD"),
    author: {
      "@type": "Person",
      name: post?.author?.name,
      url: `${getUrl()}/blog/author/${post?.author?.slug.current}`,
    },
    publisher: {
      "@type": "Organization",
      "@id": `${getUrl()}`,
      name: "Zote",
      logo: {
        "@type": "ImageObject",
        "@id": `${getOGimage()}`,
        url: `${getOGimage()}`,
        width: "600",
        height: "60",
      },
    },
    image: {
      "@type": "ImageObject",
      "@id": post?.mainImage ? urlFor(post?.mainImage).url() : "",
      url: post?.mainImage ? urlFor(post?.mainImage).url() : "",
      height: "362",
      width: "388",
    },
    url: `${getUrl()}/blog/${post?.slug.current}/`,
    isPartOf: {
      "@type": "Blog",
      "@id": `${getUrl()}/blog/`,
      name: "Manyatta - Zote's Blog",
      publisher: {
        "@type": "Organization",
        "@id": `${getUrl()}`,
        name: "Zote",
      },
    },

    //"wordCount": "488",
  };

  const breadSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        item: {
          "@type": "Webpage",
          "@id": `${getUrl()}`,
          name: "Zote",
          url: `${getUrl()}`,
        },
      },
      {
        "@type": "ListItem",
        position: 2,
        item: {
          "@type": "Blog",
          "@id": `${getUrl()}/blog`,
          name: "Manyatta - Zote's Blog",
          url: `${getUrl()}/blog`,
        },
      },
      {
        "@type": "ListItem",
        position: 3,
        item: {
          "@type": "BlogPosting",
          "@id": `${getUrl()}/blog/${post?.slug.current}/#BlogPosting`,
          name: post?.title,
          url: `${getUrl()}/blog/${post?.slug.current}`,
          datePublished: dayjs(post?.publishedAt).format("YYYY-MM-DD"),
          dateModified: dayjs(
            post?.lastUpdatedAt ? post?.lastUpdatedAt : post?.publishedAt
          ).format("YYYY-MM-DD"),
          author: {
            "@type": "Person",
            name: post?.author?.name,
            url: `${getUrl()}/blog/author/${post?.author?.slug.current}`,
          },
          mainEntityOfPage: `${getUrl()}/blog/${post?.slug.current}`,
          headline: post?.title,
          publisher: {
            "@type": "Organization",
            "@id": `${getUrl()}`,
            name: "Zote",
            logo: {
              "@type": "ImageObject",
              "@id": `${getOGimage()}`,
              url: `${getOGimage()}`,
              width: "600",
              height: "60",
            },
          },
          image: {
            "@type": "ImageObject",
            "@id": post?.mainImage ? urlFor(post?.mainImage).url() : "",
            url: post?.mainImage ? urlFor(post?.mainImage).url() : "",
            height: "362",
            width: "388",
          },
        },
      },
    ],
  };

  return (
    <>
      <Head>
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <meta charSet="utf-8" />
        <title>{`${post?.title} | Zote`}</title>
        <link rel="canonical" href={`${getUrl()}/blog/${post?.slug.current}`} />
        <link
          rel="alternate"
          type="application/rss+xml"
          href={`${getUrl()}/blog/${post?.slug.current}`}
        ></link>
        <meta name="description" content={`${post?.description}`} />
        <meta name="og:title" content={`${post?.title} | Zote`} />

        <meta name="og:description" content={`${post?.description}`} />
        <meta
          name="og:image"
          content={`${post?.mainImage ? urlFor(post?.mainImage).url() : ""}`}
        />
        <meta
          property="og:url"
          content={`${getUrl()}/blog/${post?.slug.current}`}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadSchema) }}
        />
      </Head>
      <main className=" flex flex-col items-center bg-gray-100  pt-12 lg:pt-24">
        <div className=" flex flex-col items-center justify-center space-y-10 lg:w-[60%]">
          <div className=" flex flex-shrink-0 flex-col justify-center space-y-3 px-5 lg:w-2/3 lg:items-center lg:space-y-4 lg:px-0">
            <h1 className="  text-2xl font-semibold lg:text-center lg:text-4xl ">
              {post?.title}
            </h1>
            <div className=" flex flex-row items-center space-x-0">
              <span className=" rounded-full bg-gray-300 px-3  py-1 text-sm font-bold text-gray-700 lg:bg-gray-300  lg:px-5 lg:text-base lg:text-gray-700 ">
                Published on {dayjs(post?.publishedAt).format("MMMM D, YYYY")}
              </span>
              <BsDot />
              <Link
                href={`/blog/author/${encodeURIComponent(
                  post?.author?.slug?.current
                )}`}
                passHref
              >
                <a className=" text-base font-semibold text-gray-700">
                  {post?.author?.name}
                </a>
              </Link>
            </div>
          </div>
          <div className=" relative h-56 w-11/12 rounded-md bg-gray-300  lg:h-[27rem] lg:w-full">
            <div className=" absolute bottom-1 right-10 z-10 -mr-8 -mt-10 flex -rotate-6 justify-center rounded-full  bg-zote-primary bg-opacity-80 px-6 py-1 shadow-2xl">
              <span className="z-10 text-base text-gray-100  opacity-100   drop-shadow-2xl">
                Home search, simplified
              </span>
            </div>
            <Image
              src={post?.mainImage ? urlFor(post?.mainImage).url() : ""}
              className=" h-full w-full rounded-md object-cover "
              layout="fill"
              alt={post?.title}
            />
          </div>
        </div>
        <div className=" prose mt-10 flex flex-col space-y-6 px-6 text-lg marker:text-gray-900 prose-h1:text-2xl prose-h2:text-2xl   prose-p:leading-relaxed prose-p:text-gray-900 prose-a:text-zote-primary   lg:w-2/3 lg:px-0 lg:text-xl ">
          <PortableText value={post?.body} components={ptComponents} />
        </div>
        <div className=" mt-16 flex h-72 w-full flex-col items-center justify-center space-y-5 bg-[#0D242A] py-10 lg:h-88 lg:flex-row lg:space-x-20 lg:space-y-0 lg:px-40">
          <div className=" flex flex-col space-y-1 text-gray-300">
            <h2 className="  text-center text-2xl font-semibold lg:text-justify lg:text-4xl">
              Find homes where you live
            </h2>
            <h3 className=" px-16 text-center text-lg lg:px-0 lg:text-justify">
              We have simplified the way home search is done
            </h3>
          </div>
          <div className=" group">
            <Link href="/" passHref>
              <a>
                <button className=" flex flex-row items-center space-x-2 rounded-md bg-zote-primary px-12 py-4 font-medium text-gray-100 hover:shadow-md">
                  <span className="  text-gray-100">Find homes</span>
                  <AiOutlineArrowRight className=" transition duration-300 group-hover:translate-x-1" />
                </button>
              </a>
            </Link>
          </div>
        </div>
      </main>
    </>
  );
}

export async function getStaticPaths() {
  const paths = await client.fetch(
    `*[_type == "post" && defined(slug.current)][].slug.current`
  );
  return {
    paths: paths.map((slug) => ({ params: { post: slug } })),
    fallback: true,
  };
}

export const getStaticProps = async (context) => {
  // It's important to default the slug/post so that it doesn't return "undefined"
  const { post = "" } = context.params;

  const pst = await client.fetch(
    `
  *[_type == "post" && slug.current == $post][0]
  {
    _id,
    title,
    slug,
    description,
    "author": author-> {
      name,
      bio,
      slug,
      description,
    },
    "categories": categories[]->{title, description, _id},
    mainImage,
    publishedAt,
    lastUpdatedAt,
    body[]{
      ...,
      markDefs[]{
        ...,
        _type == "internalLink" => {
          "slug": @.reference->slug
        }
      }
    }
    
  }
  `,
    { post }
  );
  if (!pst) {
    return {
      notFound: true,
    };
  }
  return {
    props: {
      post: pst,
    },
    revalidate: 5,
  };
};

export default Post;
